import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from "react-redux";
import IFCLookupList_SortHeaders from "./IFCLookupList_SortHeaders";
import IFCLookupList_Item from "./Item/IFCLookupList_Item";
import IFCLookupList_Pagination from "./IFCLookupList_Pagination";

const IFCLookupList = () => {
    const {data, sortColumn, sortMode} = useSelector(state => state.ifcLookupList);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [filters, setFilters] = useState({
        admission_type: '',
        day_or_overnight: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [searchIndex, setSearchIndex] = useState({});
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        if (!data || data.length === 0) return;

        const index = createSearchIndex();
        setSearchIndex(index);
    }, [data]);

    const admissionTypeOptions = useMemo(() => {
        return [...new Set(data.map((item) => item.admissionType).filter(Boolean))];
    }, [data]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Optimized search function using index
    const searchWithIndex = useCallback((term) => {
        if (!term) return Array.from(Array(data.length).keys()); // Return all indices if no search term

        const searchTerms = term.toLowerCase().split(/\s+/).filter(t => t.length > 1);
        if (searchTerms.length === 0) return Array.from(Array(data.length).keys());

        // Find matching indices for each search term
        const matchingSets = searchTerms.map(term => {
            const matchingIndices = new Set();

            // Check for exact matches in the index
            if (searchIndex[term]) {
                searchIndex[term].forEach(idx => matchingIndices.add(idx));
            }

            // Check for partial matches in the index
            Object.keys(searchIndex).forEach(key => {
                if (key.includes(term)) {
                    searchIndex[key].forEach(idx => matchingIndices.add(idx));
                }
            });

            return matchingIndices;
        });

        // Find indices that match all search terms (intersection)
        if (matchingSets.length === 0) return [];

        let resultIndices = Array.from(matchingSets[0]);
        for (let i = 1; i < matchingSets.length; i++) {
            resultIndices = resultIndices.filter(idx => matchingSets[i].has(idx));
        }

        return resultIndices;
    }, [searchIndex]);

    const createSearchIndex = () => {
        const index = {};

        data.forEach((item, idx) => {
            // Index description words
            if (item.description) {
                const words = item.description.toLowerCase().split(/\s+/);
                words.forEach(word => {
                    if (word.length > 2) { // Chỉ index các từ dài hơn 2 ký tự
                        if (!index[word]) index[word] = [];
                        if (!index[word].includes(idx)) index[word].push(idx);
                    }
                });
            }

            // Index item number
            if (item.itemNumber) {
                const itemNum = item.itemNumber.toLowerCase().replace(/\s+/g, '');
                if (!index[itemNum]) index[itemNum] = [];
                if (!index[itemNum].includes(idx)) index[itemNum].push(idx);
            }

            // Index DRG codes
            ['mplAhmDrg', 'ahsaDrg', 'bupaDrg', 'publicDrg'].forEach(field => {
                const value = item[field];
                if (value) {
                    const code = value.toLowerCase().replace(/\s+/g, '');
                    if (!index[code]) index[code] = [];
                    if (!index[code].includes(idx)) index[code].push(idx);
                }
            });
        });

        return index;
    };


    // Filter and search data
    const filteredData = useMemo(() => {
        if (!data.length) return [];
        setIsSearching(true);

        try {
            // Use indexed search for search term
            const matchingIndices = searchWithIndex(debouncedSearchTerm);

            // Apply filters to the matching indices
            return matchingIndices
                .map(idx => data[idx])
                .filter(item => {
                    // Apply filters
                    const admissionMatch = !filters.admission_type || item.admissionType === filters.admission_type;
                    const dayOvernightMatch = !filters.day_or_overnight || item.dayOrOvernight === filters.day_or_overnight;

                    return admissionMatch && dayOvernightMatch;
                });
        } finally {
            setIsSearching(false);
        }
    }, [data, debouncedSearchTerm, filters, searchWithIndex]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on new search
    };

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1); // Reset to first page on filter change
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            admission_type: '',
            day_or_overnight: ''
        });
        setCurrentPage(1);
    };

    // Handle pagination
    const goToPage = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
    };

    const renderRow = (item, key) => <IFCLookupList_Item key={key} ifcData={item}/>

    return (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="mb-6"><h1 className="text-2xl font-bold text-gray-900 mb-4">Search IFC Lookup</h1>
                <div className="bg-white shadow-md rounded-lg p-4 mb-5">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-grow relative">
                            <input type="text"
                                   placeholder="Search by description, item number, or DRG code..."
                                   className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                   value={searchTerm}
                                   onChange={handleSearchChange}
                            />
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition border-0">{showFilters ? 'Hide Filters' : 'Show Filters'}</button>
                    </div>
                    {showFilters && (
                        <div className="mt-4 grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Admission Type</label>
                                <select name="admission_type" value={filters.admission_type} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900">
                                    <option value="">All Types</option>
                                    {admissionTypeOptions.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Day/Overnight</label>
                                <select name="day_or_overnight" value={filters.day_or_overnight} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900">
                                    <option value="">All</option>
                                    <option value="Day">Day</option>
                                    <option value="O/N">Overnight</option>
                                </select>
                            </div>
                            <div className="col-span-3">
                                <button className="btn-reset-filter px-2 py-2 text-sm text-blue-600 hover:text-blue-800" onClick={resetFilters}>Reset Filters</button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                    <div className="text-sm text-gray-600">{loading ? (
                        'Loading...'
                    ) : (
                        `Showing ${filteredData.length > 0 ? indexOfFirstItem + 1 : 0}-${Math.min(indexOfLastItem, filteredData.length)} of ${filteredData.length} results`
                    )}</div>
                    <div className="flex items-center">
                        <label className="text-sm text-gray-600 mr-2">Items per page:</label>
                        <select className="px-2 py-1 border border-gray-300 rounded text-gray-900"
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                style={{ width: '80px' }}
                        >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>

                <table className="table procedure-list-table" cellSpacing="0" width="100%">
                    <IFCLookupList_SortHeaders/>
                    <tbody className="bg-white">
                        {currentItems.length > 0 ? (
                            currentItems.map(renderRow)
                        ) : (
                            <tr>
                                <td colSpan="100%" className="text-center py-4 text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <IFCLookupList_Pagination totalPages={totalPages} currentPage={currentPage} goToPage={goToPage} />
            </div>
        </main>
    );
}

export default IFCLookupList;